/** @format */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  AcademicCapIcon,
  ClockIcon,
  PlayIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  FunnelIcon,
  BookOpenIcon,
  BuildingOfficeIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
  RocketLaunchIcon,
  TrophyIcon,
  StarIcon
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { useCourses } from "../hooks/useCourses";
import { useEnrolledCourses } from "../hooks/useEnrolledCourses";
import { Course } from "../types";
import CourseCard from "../components/CourseCard";
import CourseDetailsModal from "../components/CourseDetailsModal";
import EditCourseModal from "../components/EditCourseModal";
import CustomDropdown from "../components/CustomDropdown";
import UserProfileLink from "../components/UserProfileLink";
import AIChatButton from '../components/AIChatButton';

import {
  formatDate,
  formatDuration,
  getInitials,
  getProgressColor,
  getCourseStatusInfo,
} from "../utils/formatUtils";

const MyCourses: React.FC = () => {
  const { currentUser } = useAuth();
  const { 
    courses, 
    loading: coursesLoading, 
    error: coursesError, 
    refreshCourses,
    fetchAuthorCourses,
    fetchAllCourses
  } = useCourses();
  const { 
    enrolledCourses, 
    loading: enrolledCoursesLoading, 
    error: enrolledCoursesError, 
    refresh: refreshEnrolledCourses,
    hasMore,
    loadMore
  } = useEnrolledCourses(currentUser?.uid);

  const [selectedStatus, setSelectedStatus] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [displayCount, setDisplayCount] = useState(6); // Show only 6 courses initially for students, all for admin/author

  const [selectedCourse, setSelectedCourse] = useState<any | null>(null);
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState<any | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Determine user role and view type
  const normalizedRole = (currentUser?.role || '').toString().trim().toLowerCase();
  const isAdmin = normalizedRole === 'admin';
  const isAuthor = normalizedRole === 'author'; // Allow authors regardless of status to see their courses
  const isStudent = normalizedRole === 'student' || (!isAdmin && !isAuthor);

  // Debounce search query to prevent excessive filtering
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch courses based on user role
  useEffect(() => {
    if (!currentUser) return;

    if (isAdmin) {
      // Admin sees all courses
      fetchAllCourses();
    } else if (isAuthor) {
      // Author sees their created courses
      fetchAuthorCourses(currentUser.uid);
    }
    // Students use the existing enrolled courses logic
  }, [currentUser, isAdmin, isAuthor, fetchAllCourses, fetchAuthorCourses]);

  // Memoized: Get courses based on user role
  const getUserCourses = useMemo(() => {
    if (!courses || courses.length === 0) return [];

    if (isAdmin) {
      // Admin sees all courses directly from the courses state
      return courses.map(course => ({
        ...course,
        // Add some additional info for admin view
        enrolled_at: course.created_at,
        expire_date: null,
        status: course.status || 'active',
        classrooms: course.classrooms || [],
      }));
    } else if (isAuthor) {
      // Author sees only their created courses - check both userId and author_id fields
      const authorCourses = courses.filter(course => 
        course.userId === currentUser?.uid || course.author_id === currentUser?.uid
      );
      return authorCourses.map(course => ({
        ...course,
        // Add some additional info for author view
        enrolled_at: course.created_at,
        expire_date: null,
        status: course.status || 'active',
        classrooms: course.classrooms || [],
      }));
    } else {
      // Students see their enrolled courses with full course data
      if (!enrolledCourses.length) return [];
    
    // Map enrolled course IDs to full course data and remove duplicates
    const userCoursesMap = new Map();
    
    enrolledCourses.forEach(enrolledCourse => {
      const course = courses.find(c => c.id === enrolledCourse.course_id);
      if (course) {
        if (!userCoursesMap.has(course.id)) {
          // Calculate expiry date: if not provided, use 3 months from enrollment
          let expireDate = enrolledCourse.expire_date;
          if (!expireDate && enrolledCourse.enrolled_at) {
            try {
              const enrolledDate = new Date(enrolledCourse.enrolled_at);
              
              // Check if the date is valid
              if (isNaN(enrolledDate.getTime())) {
                // Use current date as fallback
                const currentDate = new Date();
                const threeMonthsLater = new Date(currentDate);
                threeMonthsLater.setMonth(threeMonthsLater.getMonth() + 3);
                expireDate = threeMonthsLater.toISOString();
              } else {
                const threeMonthsLater = new Date(enrolledDate);
                threeMonthsLater.setMonth(threeMonthsLater.getMonth() + 3);
                expireDate = threeMonthsLater.toISOString();
              }
            } catch (error) {
              // Use current date as fallback
              const currentDate = new Date();
              const threeMonthsLater = new Date(currentDate);
              threeMonthsLater.setMonth(threeMonthsLater.getMonth() + 3);
              expireDate = threeMonthsLater.toISOString();
            }
          }
          
          userCoursesMap.set(course.id, {
            ...course,
            enrolled_at: enrolledCourse.enrolled_at,
            expire_date: expireDate,
            status: enrolledCourse.status,
            classrooms: enrolledCourse.classrooms,
          });
        }
      }
    });
    
    const result = Array.from(userCoursesMap.values());
    return result;
    }
  }, [courses, enrolledCourses, isAdmin, isAuthor, currentUser?.uid]);

  // Memoized: Helper functions for course progress
  const getCourseProgress = useCallback((course: any) => {
    // For now, return 0 as default progress
    // The CourseCard component will use useCourseProgress hook to get real progress
    return 0;
  }, []);



  // Memoized: Get course stats
  const stats = useMemo(() => {
    const userCourses = getUserCourses;
    const totalCourses = userCourses.length;
    
    if (isAdmin || isAuthor) {
      // Admin and Author stats
      const totalStudents = userCourses.reduce((sum, course) => sum + (course.students || 0), 0);
      const activeCourses = userCourses.filter(course => course.status === 'active' || course.status === 'published').length;
      const publishedCourses = userCourses.filter(course => course.status === 'published').length;
      
      return {
        totalCourses,
        totalStudents,
        activeCourses,
        publishedCourses,
        completedCourses: 0, // Not applicable for admin/author
        inProgressCourses: 0, // Not applicable for admin/author
        notStartedCourses: 0, // Not applicable for admin/author
        averageProgress: 0, // Not applicable for admin/author
      };
    } else {
      // Student stats
    const completedCourses = 0;
    const inProgressCourses = 0;
    const notStartedCourses = totalCourses;
    const averageProgress = 0;

    return {
      totalCourses,
      completedCourses,
      inProgressCourses,
      notStartedCourses,
      averageProgress,
        totalStudents: 0, // Not applicable for students
        activeCourses: 0, // Not applicable for students
        publishedCourses: 0, // Not applicable for students
    };
    }
  }, [getUserCourses, isAdmin, isAuthor]);

  // Memoized: Filter courses
  const filteredCourses = useMemo(() => {
    const userCourses = getUserCourses;
    let filtered = [...userCourses];

    // Don't display draft courses
    filtered = filtered.filter(course => course.status !== "draft");

    // Status filter - simplified to show all courses by default
    if (selectedStatus && selectedStatus !== "all") {
      if (selectedStatus === "completed") {
        // For now, show no completed courses since we don't have real progress data
        filtered = [];
      } else if (selectedStatus === "active") {
        // For now, show no active courses since we don't have real progress data
        filtered = [];
      } else if (selectedStatus === "not-started") {
        // Show all courses as not started since we don't have real progress data
        filtered = [...userCourses];
      }
    }

    // Search filter - use debounced query to prevent excessive filtering
    if (debouncedSearchQuery) {
      const searchTerm = debouncedSearchQuery.toLowerCase();
      filtered = filtered.filter(
        (course) =>
          course.name.toLowerCase().includes(searchTerm) ||
          (course.description && course.description.toLowerCase().includes(searchTerm)) ||
          (course.author && course.author.name && course.author.name.toLowerCase().includes(searchTerm))
      );
    }

    return filtered;
  }, [getUserCourses, selectedStatus, debouncedSearchQuery]);

  // Memoized: Get displayed courses (for lazy loading)
  const displayedCourses = useMemo(() => {
    // Admin and Author see all courses, students see limited count
    if (isAdmin || isAuthor) {
      return filteredCourses;
    }
    return filteredCourses.slice(0, displayCount);
  }, [filteredCourses, displayCount, isAdmin, isAuthor]);

  // Load more courses
  const loadMoreCourses = useCallback(() => {
    loadMore();
  }, [loadMore]);

  // Handle edit course
  const handleEditCourse = useCallback((course: any) => {
    setEditingCourse(course);
    setShowEditModal(true);
  }, []);

  // Handle edit modal close
  const handleEditModalClose = useCallback(() => {
    setShowEditModal(false);
    setEditingCourse(null);
  }, []);

  // Fetch enrolled courses when user is available
  useEffect(() => {
    if (currentUser?.uid) {
      // Only refresh if we don't have any courses loaded
      if (enrolledCourses.length === 0) {
        refreshEnrolledCourses();
      }
    }
  }, [currentUser?.uid, refreshEnrolledCourses, enrolledCourses.length]);

  // Reset display count when filters change
  useEffect(() => {
    setDisplayCount(6);
  }, [selectedStatus, searchQuery]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 overflow-x-hidden">
      {/* Hero Section */}
      <section className="hero-section relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 overflow-hidden">
        {/* Main Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12" style={{ width: '100%', maxWidth: '100vw' }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8 items-center" style={{ width: '100%' }}>
            
            {/* Left Side - Text Content */}
            <div className="text-center lg:text-left space-y-3 sm:space-y-4" style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
              {/* Welcome Badge */}
              <div className="inline-flex items-center px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-white text-xs font-medium">
                <BookOpenIcon className="h-3 w-3 mr-1.5 text-blue-300" />
                {isAdmin ? 'Admin Dashboard' : isAuthor ? 'Author Dashboard' : 'Your Learning Journey'}
              </div>

              {/* Main Headline */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-white">
                <span className="block">
                  {isAdmin ? 'Manage All' : isAuthor ? 'Manage Your' : 'Master Your'}
                </span>
                <span className="block bg-gradient-to-r from-blue-200 via-cyan-200 to-indigo-200 bg-clip-text text-transparent">
                  {isAdmin ? 'Courses' : isAuthor ? 'Courses' : 'Course Progress'}
                </span>
            </h1>

              {/* Subtitle */}
              <p className="text-base sm:text-lg text-blue-100 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                {isAdmin 
                  ? 'Monitor and manage all courses in the platform. View course performance, author details, and system-wide analytics.'
                  : isAuthor 
                  ? 'Manage your created courses, track student enrollment, and monitor course performance and engagement.'
                  : 'Track your learning progress, continue where you left off, and achieve your professional goals with expert-led courses.'
                }
              </p>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-2 py-3" style={{ width: '100%', maxWidth: '100%' }}>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">
                    {stats.totalCourses}
                  </div>
                  <div className="text-xs text-blue-200">
                    {isAdmin ? 'All Courses' : isAuthor ? 'My Courses' : 'Total Courses'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">
                    {isAdmin || isAuthor ? stats.totalStudents : stats.averageProgress}%
                  </div>
                  <div className="text-xs text-blue-200">
                    {isAdmin || isAuthor ? 'Total Students' : 'Avg Progress'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">
                    {isAdmin ? stats.activeCourses : isAuthor ? stats.publishedCourses : stats.completedCourses}
                  </div>
                  <div className="text-xs text-blue-200">
                    {isAdmin ? 'Active' : isAuthor ? 'Published' : 'Completed'}
                  </div>
                </div>
              </div>

              {/* Call-to-Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center lg:justify-start" style={{ width: '100%', maxWidth: '100%' }}>
                <button
                  onClick={() => {
                    const searchInput = document.querySelector('input[type="text"]') as HTMLInputElement;
                    searchInput?.focus();
                  }}
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-lg text-sm"
                >
                  <RocketLaunchIcon className="h-4 w-4 mr-1.5" />
                  {isAdmin || isAuthor ? 'Search Courses' : 'Search Courses'}
                </button>
                {isAdmin ? (
                  <Link
                    to="/app/admin"
                    className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold rounded-lg hover:bg-white/20 transition-all duration-200 text-sm"
                  >
                    <SparklesIcon className="h-4 w-4 mr-1.5" />
                    Admin Panel
                  </Link>
                ) : isAuthor ? (
                  <Link
                    to="/app/admin"
                    className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold rounded-lg hover:bg-white/20 transition-all duration-200 text-sm"
                  >
                    <SparklesIcon className="h-4 w-4 mr-1.5" />
                    Create Course
                  </Link>
                ) : (
                <Link
                  to="/app/catalog"
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold rounded-lg hover:bg-white/20 transition-all duration-200 text-sm"
                >
                  <SparklesIcon className="h-4 w-4 mr-1.5" />
                  Browse Catalog
                </Link>
                )}
              </div>
            </div>

            {/* Right Side - Visual Elements - Hidden on mobile for stability */}
            <div className="relative hidden xl:block">
              {/* Course Progress Cards */}
              <div className="space-y-4">
                {/* Active Course Card */}
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full flex items-center justify-center">
                      <PlayIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Active Learning</h3>
                      <p className="text-blue-200 text-sm">{stats.inProgressCourses} courses in progress</p>
                    </div>
                  </div>
                </div>

                {/* Achievement Card */}
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 ml-8">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center">
                      <TrophyIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Achievements</h3>
                      <p className="text-blue-200 text-sm">{stats.completedCourses} courses completed</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-8 sm:h-12 text-gray-50" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25" fill="currentColor"></path>
            <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5" fill="currentColor"></path>
            <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" fill="currentColor"></path>
          </svg>
        </div>
      </section>

      {/* Main Dashboard Content */}
      <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8 pt-0 sm:pt-6 relative z-20 space-y-4 sm:space-y-8">
        {/* Filters and Search */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 sm:mb-6 px-3 sm:px-0">
          <div className="flex items-center space-x-3">
            <CustomDropdown
              value={selectedStatus}
              onChange={setSelectedStatus}
              options={[
                { value: "all", label: "All Courses" },
                { value: "active", label: "In Progress" },
                { value: "completed", label: "Completed" },
                { value: "not-started", label: "Not Started" }
              ]}
              placeholder="Select Status"
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            />
          </div>
          
          {/* Search Bar */}
          <div className="relative flex-1 sm:flex-none sm:w-80">
            <input
              type="text"
              placeholder="Search your courses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 pl-10 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <svg
              className="absolute left-3 top-2.5 h-4 w-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          
          <div className="flex items-center space-x-3">
          <div className="text-sm text-gray-600">
            {stats.totalCourses} course{stats.totalCourses !== 1 ? 's' : ''}
            </div>
            <button
              onClick={refreshEnrolledCourses}
              disabled={enrolledCoursesLoading}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-4 w-4 mr-2 ${enrolledCoursesLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Course Grid */}
        <div className="px-3 sm:px-0">
        {(coursesLoading || (enrolledCoursesLoading && isStudent)) ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your courses...</p>
          </div>
        ) : (coursesError || (enrolledCoursesError && isStudent)) ? (
          <div className="text-center py-12">
            <div className="text-gray-600 text-lg mb-4">
              {coursesError && <p>Courses Error: {coursesError}</p>}
              {enrolledCoursesError && <p>Enrolled Courses Error: {enrolledCoursesError}</p>}
            </div>
            <Link
              to="/app/admin"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all duration-200 shadow-lg"
            >
              <BuildingOfficeIcon className="h-5 w-5 mr-2" />
              View Courses
            </Link>
          </div>
        ) : getUserCourses.length === 0 ? (
          <div className="text-center py-12">
            <AcademicCapIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {isAdmin ? 'No courses found' : isAuthor ? 'View or create your courses or manage' : 'No courses enrolled yet'}
            </h3>
            <p className="text-gray-600">
              {isAdmin 
                ? 'There are no courses in the system yet.'
                : isAuthor 
                ? 'Access the admin panel to view, create, or manage your courses.'
                : 'Start your learning journey by enrolling in courses from our catalog.'
              }
            </p>
          </div>
        ) : displayedCourses.length === 0 ? (
          <div className="text-center py-12">
            {searchQuery.trim() ? (
              <>
                <svg
                  className="h-16 w-16 text-gray-400 mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No courses found
                </h3>
                <p className="text-gray-600 mb-4">
                  No courses match your search "{searchQuery}". Try adjusting your search terms.
                </p>
                <button
                  onClick={() => setSearchQuery('')}
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  Clear search
                </button>
              </>
            ) : (
              <>
                <FunnelIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No courses match your filters
                </h3>
                <p className="text-gray-600">
                  Try adjusting your filters to see more courses.
                </p>
              </>
            )}
          </div>
        ) : (
          <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 -mx-3 sm:mx-0">
              {displayedCourses.map((course: any) => (
              <CourseCard
                key={course.id}
                course={course}
                showProgress={isStudent} // Only show progress for students
                showEditButton={isAdmin || isAuthor} // Show edit button for admin/author
                onClick={() => {
                  setSelectedCourse(course);
                  setShowCourseModal(true);
                }}
                onEdit={() => handleEditCourse(course)}
              />
            ))}
          </div>
            
            {/* Load More Button - Only show for students */}
            {hasMore && isStudent && (
              <div className="text-center mt-8">
                <button
                  onClick={loadMoreCourses}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Load More Courses
                </button>
              </div>
            )}
          </>
        )}
        </div>
      </div>
      
      {/* AI Chat Button */}
      <AIChatButton />

      {/* Course Preview Modal */}
      <CourseDetailsModal
        isOpen={showCourseModal}
        onClose={() => setShowCourseModal(false)}
        course={selectedCourse}
        onAddToCart={() => {}}
        onToggleFavorite={() => {}}
        isFavorite={false}
        isInCart={false}
      />

      {/* Edit Course Modal */}
      <EditCourseModal
        isOpen={showEditModal}
        onClose={handleEditModalClose}
        course={editingCourse}
      />
    </div>
  );
};

export default MyCourses;
